import { roomTemplates } from "../data/rooms";
import { equipmentTypes } from "../data/equipmentTypes";

const STORAGE_KEY = "grow-room-audit-board-v2";
const DEVICE_KEY_STORAGE = "grow-room-audit-device-key";

/* ── device key (anonymous persistence) ── */

function getDeviceKey() {
  let key = localStorage.getItem(DEVICE_KEY_STORAGE);
  if (!key) {
    key = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY_STORAGE, key);
  }
  return key;
}

/* ── local persistence ── */

export function loadState() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return structuredClone(roomTemplates);

  try {
    const parsed = JSON.parse(saved);
    return roomTemplates.map((template) => {
      const savedRoom = parsed.find((room) => room.id === template.id);
      return savedRoom
        ? { ...structuredClone(template), ...savedRoom }
        : structuredClone(template);
    });
  } catch {
    return structuredClone(roomTemplates);
  }
}

export function persistState(rooms) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rooms));
}

/* ── remote API helpers (best-effort) ── */

const API_BASE = "/api/rooms";

export async function fetchRemoteState() {
  try {
    const key = getDeviceKey();
    const res = await fetch(`${API_BASE}?key=${encodeURIComponent(key)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data;
  } catch {
    return null;
  }
}

export async function pushRemoteState(rooms) {
  try {
    const key = getDeviceKey();
    await fetch(API_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, rooms }),
    });
  } catch {
    // silent — localStorage is the source of truth
  }
}

/* ── controls mutation helpers ── */

export function ensureControls(room) {
  if (!room.controls) {
    room.controls = {
      roomId: room.id,
      equipment: [],
      standalones: {
        damatexCage: { present: false, subSensors: { temp: false, humidity: false, co2: false }, calibrationNeeded: false, lastCalDate: null },
        lightIntrusion: { present: false },
        rotorFlow: { present: false },
      },
      outputs: [
        { id: "out_fert", type: "fertilizer", label: "Fertilizer Output" },
        { id: "out_water", type: "fresh_water", label: "Fresh Water Output" },
      ],
    };
  }
  return room.controls;
}

export function getNextEquipmentNumber(room, eqType) {
  const controls = ensureControls(room);
  const existing = controls.equipment.filter((e) => e.type === eqType);
  if (existing.length === 0) return 1;
  const nums = existing.map((e) => {
    const match = e.label.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  });
  return Math.max(...nums) + 1;
}

export function addEquipment(room, eqType) {
  const controls = ensureControls(room);
  const num = getNextEquipmentNumber(room, eqType);
  const entry = {
    id: `${eqType}_${Date.now()}`,
    type: eqType,
    label: `${equipmentTypes[eqType].label} ${num}`,
    stage: "veg",
    mode: "auto",
    sensors: [],
    service: null,
  };
  controls.equipment.push(entry);
  return entry;
}

export function removeEquipment(room, equipmentId) {
  const controls = ensureControls(room);
  controls.equipment = controls.equipment.filter((e) => e.id !== equipmentId);
}

export function setEquipmentStage(room, equipmentId, stage) {
  const controls = ensureControls(room);
  const eq = controls.equipment.find((e) => e.id === equipmentId);
  if (eq) eq.stage = stage;
}

export function setEquipmentMode(room, equipmentId, mode) {
  const controls = ensureControls(room);
  const eq = controls.equipment.find((e) => e.id === equipmentId);
  if (eq) eq.mode = mode;
}

export function getDefaultSensorSource(equipmentType) {
  const typeDef = equipmentTypes[equipmentType];
  return typeDef?.defaultSensor || "temp";
}

export function setEquipmentService(room, equipmentId, serviceConfig) {
  const controls = ensureControls(room);
  const eq = controls.equipment.find((e) => e.id === equipmentId);
  if (eq) eq.service = serviceConfig;
}

export function clearEquipmentService(room, equipmentId) {
  const controls = ensureControls(room);
  const eq = controls.equipment.find((e) => e.id === equipmentId);
  if (eq) eq.service = null;
}

export function attachSensor(room, equipmentId, sensorType) {
  const controls = ensureControls(room);
  const eq = controls.equipment.find((e) => e.id === equipmentId);
  if (eq && !eq.sensors.find((s) => s.type === sensorType)) {
    eq.sensors.push({ type: sensorType, present: true });
  }
}

export function detachSensor(room, equipmentId, sensorType) {
  const controls = ensureControls(room);
  const eq = controls.equipment.find((e) => e.id === equipmentId);
  if (eq) {
    eq.sensors = eq.sensors.filter((s) => s.type !== sensorType);
  }
}

export function updateStandalone(room, key, value) {
  const controls = ensureControls(room);
  const parts = key.split(".");
  let target = controls.standalones;
  for (let i = 0; i < parts.length - 1; i++) {
    target = target[parts[i]];
  }
  target[parts[parts.length - 1]] = value;
}

export function updateDamatexSubSensor(room, subSensorKey, value) {
  const controls = ensureControls(room);
  controls.standalones.damatexCage.subSensors[subSensorKey] = value;
}

export function getEligibleSensors(room, equipmentId) {
  const controls = ensureControls(room);
  const eq = controls.equipment.find((e) => e.id === equipmentId);
  if (!eq) return [];
  const typeDef = equipmentTypes[eq.type];
  if (!typeDef) return [];
  const attached = new Set(eq.sensors.map((s) => s.type));
  return typeDef.allowedSensors.filter((s) => !attached.has(s));
}

/* ── board mutation helpers ── */

export function getAllPlacedIds(room) {
  return Object.values(room.board).flat().filter(Boolean);
}

export function getComponent(room, componentId) {
  return room.components.find((c) => c.id === componentId);
}

export function getPlacedComponent(room, componentId) {
  const component = getComponent(room, componentId);
  if (!component) return null;

  for (const [spotType, slots] of Object.entries(room.board)) {
    const slotIndex = slots.findIndex((id) => id === componentId);
    if (slotIndex >= 0) return { component, spotType, slotIndex };
  }
  return null;
}

export function connectionExists(room, fromId, toId) {
  return room.connections.some(([a, b]) => a === fromId && b === toId);
}

export function addConnection(room, fromId, toId) {
  if (fromId === toId || connectionExists(room, fromId, toId)) return false;
  room.connections.push([fromId, toId]);
  return true;
}

export function removeConnectionsFor(room, componentId) {
  room.connections = room.connections.filter(
    ([fromId, toId]) => fromId !== componentId && toId !== componentId
  );
}

export function removeFromBoard(room, componentId) {
  for (const slots of Object.values(room.board)) {
    const index = slots.findIndex((id) => id === componentId);
    if (index >= 0) slots[index] = null;
  }
  removeConnectionsFor(room, componentId);
}

export function moveToSlot(room, componentId, targetType, targetIndex) {
  const component = getComponent(room, componentId);
  if (!component || component.spotType !== targetType) return false;
  removeFromBoard(room, componentId);
  room.board[targetType][targetIndex] = componentId;
  return true;
}

export function getLibraryComponents(room) {
  const placed = new Set(getAllPlacedIds(room));
  return room.components.filter((c) => !placed.has(c.id));
}

export function validateRoom(room) {
  const checks = [];
  const placedIds = getAllPlacedIds(room);
  const connectionMap = new Map();

  room.connections.forEach(([fromId, toId]) => {
    if (!connectionMap.has(fromId)) connectionMap.set(fromId, []);
    connectionMap.get(fromId).push(toId);
  });

  room.components.forEach((component) => {
    const placement = getPlacedComponent(room, component.id);
    if (!placement) {
      checks.push({
        ok: false,
        title: component.name + " is missing",
        detail:
          component.type +
          " needs to be placed on a " +
          component.spotType +
          " board spot.",
      });
      return;
    }

    if (component.type === "service") {
      const targets = connectionMap.get(component.id) || [];
      if (!targets.includes(component.expectedSensorId)) {
        const sensor = getComponent(room, component.expectedSensorId);
        checks.push({
          ok: false,
          title: component.name + " is not connected correctly",
          detail:
            "Service with sensor must connect to " +
            (sensor ? sensor.name : "the correct sensor") +
            ".",
        });
      } else {
        checks.push({
          ok: true,
          title: component.name + " is linked",
          detail: "Service is connected to the required sensor.",
        });
      }
    }

    if (component.type === "virtual") {
      const targets = connectionMap.get(component.id) || [];
      if (!component.formula || !component.formula.trim()) {
        checks.push({
          ok: false,
          title: component.name + " has no formula",
          detail:
            "Virtuals can include anything in formulas, but the formula cannot be empty.",
        });
      } else if (targets.length === 0) {
        checks.push({
          ok: false,
          title: component.name + " is not connected",
          detail: "A virtual should not connect to nothing.",
        });
      } else {
        checks.push({
          ok: true,
          title: component.name + " is active",
          detail:
            "Formula is present and the virtual is linked into the board.",
        });
      }
    }
  });

  if (room.controls) {
    room.controls.equipment.forEach((eq) => {
      if (!eq.service) {
        checks.push({
          ok: false,
          title: eq.label + " has no service configured",
          detail: "Connect a service with sensor to complete equipment setup.",
        });
      } else {
        checks.push({
          ok: true,
          title: eq.label + " service configured",
          detail: `Linked to ${eq.service.sensorSource} sensor.`,
        });
      }
    });
  }

  const unplacedOutputs = room.components.filter(
    (c) => c.type === "output" && !placedIds.includes(c.id)
  );
  const unplacedSensors = room.components.filter(
    (c) => c.type === "sensor" && !placedIds.includes(c.id)
  );

  if (unplacedOutputs.length === 0) {
    checks.push({
      ok: true,
      title: "Outputs are placed",
      detail: "Every output has a spot on the board.",
    });
  }

  if (unplacedSensors.length === 0) {
    checks.push({
      ok: true,
      title: "Sensors are placed",
      detail: "Every sensor has a spot on the board.",
    });
  }

  return checks;
}

export function getFlagSummary(room) {
  const checks = validateRoom(room);
  const failing = checks.filter((c) => !c.ok);
  return { checks, failing };
}

export function getRoomEventsForDay(rooms, dayIndex) {
  return rooms.flatMap((room) =>
    room.weeklyEvents
      .filter((event) => event.day === dayIndex)
      .map((event) => ({ ...event, roomId: room.id, roomStatus: room.status }))
  );
}

export function getResolveRooms(rooms) {
  return rooms.filter(
    (room) => room.recentHarvest || room.weeklyEvents.length <= 2
  );
}
